import { buildApiUrl } from "../../../config";
import { withCredentials } from "../../../services/apiClient";
import { fetchWithRetry, readApiError } from "../../../utils";

import {
  buildPathWithQuery,
  fetchSlideLibraryJson,
  isEmptyLibraryResponse,
  readJsonResponse,
  readListPayload,
} from "../http";
import { normalizeSlideLibraryCollection, normalizeSlideLibrarySlide } from "../normalizers";
import type {
  SlideLibraryCollection,
  SlideLibraryCollectionsResponse,
  SlideLibraryListOptions,
  SlideLibrarySlide,
  SlideLibrarySlidesResponse,
} from "../types";
import {
  COLLECTION_HYDRATION_CONCURRENCY,
  mapWithConcurrency,
} from "../../concurrency";

const READY_STATUS = "READY";

function markCompanyCollection(collection: SlideLibraryCollection): SlideLibraryCollection {
  return {
    ...collection,
    source: "company",
    readOnly: true,
  };
}

function markCompanySlide(slide: SlideLibrarySlide, collectionId: string): SlideLibrarySlide {
  return {
    ...slide,
    source: "company",
    collection_id: collectionId,
  };
}

export async function fetchCompanySlideCollections(
  companyId: string,
  options: Pick<SlideLibraryListOptions, "page" | "pageSize"> = {}
): Promise<SlideLibraryCollectionsResponse> {
  const basePath = buildPathWithQuery("/api/finster/company-slide-collections", options);
  const separator = basePath.includes("?") ? "&" : "?";
  const path = `${basePath}${separator}company_id=${encodeURIComponent(companyId)}`;

  const response = await fetchWithRetry(
    buildApiUrl(path),
    withCredentials({ method: "GET" }),
    1,
    "CompanySlideCollections"
  );

  if (!response.ok) {
    if (isEmptyLibraryResponse(response)) {
      return { collections: [], totalCount: 0 };
    }
    throw new Error(await readApiError(response));
  }

  const payload = await readJsonResponse(response);
  const data = readListPayload(payload);
  const rawCollections = Array.isArray(data.collections) ? data.collections : [];
  const collections = rawCollections
    .map((collection) => normalizeSlideLibraryCollection(collection))
    .filter((collection): collection is SlideLibraryCollection => Boolean(collection))
    .map(markCompanyCollection)
    .filter((collection) => {
      const status = (collection.status || "").toUpperCase();
      return !status || status === READY_STATUS;
    });

  return {
    collections,
    totalCount: Number(data.total_count ?? data.totalCount ?? collections.length),
  };
}

export async function fetchCompanySlideCollectionSlides(
  collectionId: string,
  options: SlideLibraryListOptions = {},
  requestOptions: Pick<RequestInit, "signal"> = {}
): Promise<SlideLibrarySlidesResponse> {
  const response = await fetchWithRetry(
    buildApiUrl(
      buildPathWithQuery(
        `/api/finster/company-slide-collections/${encodeURIComponent(collectionId)}/slides`,
        options
      )
    ),
    withCredentials({ method: "GET", signal: requestOptions.signal }),
    1,
    "CompanySlideCollectionSlides"
  );

  if (!response.ok) {
    if (isEmptyLibraryResponse(response)) {
      return { slides: [], totalCount: 0 };
    }
    throw new Error(await readApiError(response));
  }

  const payload = await readJsonResponse(response);
  const data = readListPayload(payload);
  const slides = Array.isArray(data.slides)
    ? data.slides
        .map((slide) => normalizeSlideLibrarySlide(slide))
        .filter((slide): slide is SlideLibrarySlide => Boolean(slide))
        .map((slide) => markCompanySlide(slide, collectionId))
        .filter((slide) => Boolean(slide.slide_storage_path || slide.slide_url || slide.download_url))
    : [];

  return {
    slides,
    totalCount: Number(data.total_count ?? data.totalCount ?? slides.length),
  };
}

export async function favouriteCompanySlideCollectionSlide(
  collectionId: string,
  slideId: string
): Promise<void> {
  await fetchSlideLibraryJson<unknown>(
    `/api/finster/company-slide-collections/${encodeURIComponent(collectionId)}/slides/${encodeURIComponent(slideId)}/favourite`,
    { method: "PUT" },
    "CompanySlideFavourite"
  );
}

export async function unfavouriteCompanySlideCollectionSlide(
  collectionId: string,
  slideId: string
): Promise<void> {
  await fetchSlideLibraryJson<unknown>(
    `/api/finster/company-slide-collections/${encodeURIComponent(collectionId)}/slides/${encodeURIComponent(slideId)}/favourite`,
    { method: "DELETE" },
    "CompanySlideUnfavourite"
  );
}

export async function fetchCompanyFavouriteSlides(
  companyId: string
): Promise<SlideLibrarySlide[]> {
  const { collections } = await fetchCompanySlideCollections(companyId, {
    page: 1,
    pageSize: 100,
  });
  const slidePages = await mapWithConcurrency(
    collections,
    COLLECTION_HYDRATION_CONCURRENCY,
    (collection) =>
      fetchCompanySlideCollectionSlides(collection.collection_id, {
        page: 1,
        pageSize: 1000,
      })
  );
  return slidePages.flatMap((result) => result.slides.filter((slide) => slide.favourite));
}
